import React, { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { PaymentElement } from "@stripe/react-stripe-js";
import { toast, ToastContainer } from "react-toastify";
import { useAppDispatch } from "../redux/hooks";
import { clearCart } from "../redux/slices/cartSlice";
import { useNavigate } from "react-router-dom";
import { getAuthCookie } from "../utils/authCookies";

interface PaymentFormProps {
  supplierId: string;
  totalAmount: number;
  dueAmount: number;
  orderId: string;
  selectedPaymentMethod: string;
  onPaymentSuccess: (paymentIntentId: string) => void | Promise<void>;
  userId: string;
  userEmail: string;
  redirectUrl?: string; // Optional redirect URL after payment
}

type SavedCard = {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
};

const brandLogo = {
  visa: (
    <svg width="60" height="22" viewBox="0 0 60 22" fill="none">
      <text x="0" y="18" fontSize="20" fontWeight="bold" fill="#fff" fontFamily="Arial">VISA</text>
    </svg>
  ),
  mastercard: (
    <svg width="48" height="22" viewBox="0 0 48 22" fill="none">
      <circle cx="16" cy="11" r="10" fill="#EA001B" />
      <circle cx="32" cy="11" r="10" fill="#FFA200" fillOpacity="0.9" />
    </svg>
  ),
  amex: (
    <svg width="54" height="22" viewBox="0 0 54 22" fill="none">
      <rect width="54" height="22" rx="5" fill="#0077A6" />
      <text x="8" y="15" fontSize="12" fontWeight="bold" fill="#fff" fontFamily="Arial">AMEX</text>
    </svg>
  ),
  discover: (
    <svg width="54" height="22" viewBox="0 0 54 22" fill="none">
      <rect width="54" height="22" rx="5" fill="#f76b1c" />
      <text x="6" y="15" fontSize="13" fontWeight="bold" fill="#fff" fontFamily="Arial">DISCOVER</text>
    </svg>
  ),
  diners: (
    <svg width="54" height="22" viewBox="0 0 54 22" fill="none">
      <rect width="54" height="22" rx="5" fill="#0061A8" />
      <text x="5" y="15" fontSize="12" fontWeight="bold" fill="#fff" fontFamily="Arial">DINERS</text>
    </svg>
  ),
  jcb: (
    <svg width="48" height="22" viewBox="0 0 48 22" fill="none">
      <rect width="48" height="22" rx="5" fill="#0a3b7e" />
      <text x="7" y="15" fontSize="13" fontWeight="bold" fill="#fff" fontFamily="Arial">JCB</text>
    </svg>
  ),
  unionpay: (
    <svg width="60" height="22" viewBox="0 0 60 22" fill="none">
      <rect width="60" height="22" rx="5" fill="#0aa1e1" />
      <text x="7" y="15" fontSize="13" fontWeight="bold" fill="#fff" fontFamily="Arial">UNIONPAY</text>
    </svg>
  ),
  Default: (
    <svg width="44" height="22" viewBox="0 0 44 22" fill="none">
      <rect width="44" height="22" rx="5" fill="#fff" fillOpacity="0.15" />
      <text x="9" y="16" fontSize="10" fontWeight="bold" fill="#fff" fontFamily="Arial">CARD</text>
    </svg>
  ),
};


const PaymentForm: React.FC<PaymentFormProps> = ({ supplierId, totalAmount, dueAmount, orderId, selectedPaymentMethod, onPaymentSuccess, userId, userEmail, redirectUrl }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const token = getAuthCookie("sessionToken") || "";
  const [activeTab, setActiveTab] = useState("pills-bill-info-tab");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // --- New State ---
  const [saveCard, setSaveCard] = useState(false);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);


  useEffect(() => {
    const loadSavedCards = async () => {
      // console.log("Loading saved cards...");
      try {
        setLoadingCards(true);
        const cards = await fetchSavedCards();
        // console.log("Saved Cards :", cards)
        setSavedCards(
          cards.map((c) => ({
            id: c._id,
            brand: c.card_brand,
            last4: c.last4,
            exp_month: c.exp_month,
            exp_year: c.exp_year,
          }))
        );
      } catch (err) {
        // toast.error(
        //   err instanceof Error ? err.message : "Failed to load saved cards"
        // );
      } finally {
        setLoadingCards(false);
      }
    };
    loadSavedCards();
  }, [token, supplierId, userId]);

  const fetchSavedCards = async (): Promise<any[]> => {
    const sellerOrganizationId = String(supplierId || "").trim();
    const customerMembershipId = String(userId || "").trim();
    if (!token || !sellerOrganizationId || !customerMembershipId) return [];

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/payments/stripe/organizations/${encodeURIComponent(sellerOrganizationId)}/memberships/${encodeURIComponent(customerMembershipId)}/saved-cards`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    const data = await res.json();
    // Accept both formats:
    if (Array.isArray(data)) return data;
    if (data.status && Array.isArray(data.data)) return data.data;
    if (data.success && Array.isArray(data.data)) return data.data;
    throw new Error(data.error || "Failed to fetch saved cards");
  };

  const fetchSavedCardById = async (cardId: string): Promise<any> => {
    // console.log("fetchSavedCardById IN");
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/payments/stripe/saved-cards/${cardId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    const data = await res.json();
    // Defensive: If wrapped with status/data
    if (data.status && data.data) return data.data;
    // If directly the card object
    if (data._id && data.payment_method_id) return data;
    // Otherwise, it's an error
    throw new Error(data.error || "Failed to fetch saved card");
  };


  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orders/${orderId}/payment`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            payment_id: paymentIntentId, // Use Stripe's Payment Intent ID
            total_amount: Math.round(Number(totalAmount) * 100) / 100,
            amount_paid: Math.round(Number(totalAmount) * 100) / 100,
            amount_due: dueAmount,
            payment_date: new Date().toISOString(),
            method: selectedPaymentMethod,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update payment status in order");
      }

      // console.log("Payment Update Response:", await response.json());
      // console.log("Payment Intent ID:", response);
      toast.success("Payment updated successfully!");
      dispatch(clearCart());
      localStorage.removeItem("cart_id");
      // Await so parent async work (e.g. emails) finishes before refresh/navigation.
      await Promise.resolve(onPaymentSuccess(paymentIntentId));
      if (redirectUrl) {
        navigate(redirectUrl);
      } else {
        navigate(0);
      }
    } catch (error) {
      const errMessage = (error as Error).message || "An unknown error occurred";
      console.error("Payment Update Failed:", errMessage);
      toast.error(`Error: ${errMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // const handlePaymentSubmit = async (event: React.FormEvent) => {
  //   event.preventDefault(); // Prevent page refresh

  //   if (!stripe || !elements) {
  //     toast.error("Stripe is not fully loaded. Please try again.");
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     if (saveCard) {
  //       await createSetupIntent();
  //       setLoading(false);
  //       return;
  //     }
  //     // Call backend to create a PaymentIntent
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${supplierId}`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({
  //           amount: totalAmount * 100,
  //           currency: "usd",
  //           payment_method_types: ["card"],
  //         }),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error(`Payment Intent creation failed: ${response.statusText}`);
  //     }

  //     const data = await response.json();
  //     // console.log("Payment Intent Response:", data);
  //     const clientSecret = data.clientSecret || data.client_secret; // Ensure correct property name

  //     if (!clientSecret) {
  //       throw new Error("Payment failed: Invalid client secret");
  //     }

  //     // Get the CardElement
  //     const cardElement = elements.getElement(CardElement);
  //     if (!cardElement) {
  //       throw new Error("CardElement not found. Please try again.");
  //     }

  //     // Confirm payment with Stripe
  //     const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
  //       payment_method: {
  //         card: cardElement,
  //       },
  //     });

  //     if (error) {
  //       throw new Error(error.message || "Payment failed.");
  //     }

  //     // Success: Payment confirmed!
  //     toast.success("Payment successfull !");

  //     // Update order payment status
  //     await handlePaymentSuccess(paymentIntent.id);

  //   } catch (err) {
  //     console.error("Payment failed:", err);
  //     toast.error(err instanceof Error ? err.message : "Payment failed. Please try again.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.error("Stripe is not ready");
      return;
    }

    setLoading(true);

    try {
      // ✅ Submit PaymentElement first
      await elements.submit(); // <--- REQUIRED for confirmSetup to work properly

      let paymentMethodId: string | null = null;
      let stripeCustomerId: string | null = null;

      // If user wants to save the card, create a SetupIntent first
      if (saveCard) {
        const setupIntentResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/stripe/organizations/${supplierId}/setup-intent`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email: userEmail }),
          }
        );

        if (!setupIntentResponse.ok) {
          throw new Error("Failed to create SetupIntent");
        }

        const { client_secret, stripe_customer_id } = await setupIntentResponse.json();
        stripeCustomerId = stripe_customer_id;

        const setupResult = await stripe.confirmSetup({
          elements,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                email: userEmail,
              },
            },
          },
          redirect: "if_required",
          clientSecret: client_secret,
        });

        if (setupResult.error) {
          throw new Error(setupResult.error.message || "SetupIntent failed");
        }

        paymentMethodId = setupResult.setupIntent?.payment_method as string;

        // Save the card on backend
        await fetch(`${process.env.REACT_APP_API_URL}/payments/stripe/save-card-manually`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            organization_id: supplierId,
            payment_method_id: paymentMethodId,
            stripe_customer_id: stripeCustomerId,
          }),
        });

        toast.success("Card saved successfully!");
      }

      // Either saved or not, now charge the card using the default PaymentElement
      const paymentIntentRes = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (paymentIntentRes.error) {
        throw new Error(paymentIntentRes.error.message);
      }

      toast.success("Payment successful!");
      await handlePaymentSuccess(paymentIntentRes.paymentIntent.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Payment failed.");
    } finally {
      setLoading(false);
    }
  };

  const createSetupIntent = async () => {
    try {
      // console.log("Creating SetupIntent...");
      // 1. Create SetupIntent via your backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payments/stripe/organizations/${supplierId}/setup-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: userEmail }),
        }
      );
      if (!response.ok) throw new Error("Failed to create SetupIntent");
      const { client_secret, stripe_customer_id } = await response.json();
      // console.log("SetupIntent Response:", { client_secret, stripe_customer_id });

      // 2. Confirm card setup with Stripe
      if (!stripe) throw new Error("Stripe is not loaded.");
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) throw new Error("CardElement not found.");

      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: cardElement,
          billing_details: { email: userEmail },
        },
      });

      if (!result || result.error) {
        throw new Error(result?.error?.message || "Card setup failed.");
      }

      // console.log("SetupIntent Result:", result);
      const paymentMethodId = result.setupIntent.payment_method;
      if (typeof paymentMethodId === "string") {
        // Call your backend to save card
        const saveRes = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/stripe/save-card-manually`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              user_id: userId,
              organization_id: supplierId,
              payment_method_id: paymentMethodId,
              stripe_customer_id: stripe_customer_id,
            }),
          }
        );
        const saveData = await saveRes.json();
        // console.log("Save Card Response:", saveData);
        toast.success("Card saved successfully!");
        await chargeSavedCard(paymentMethodId, stripe_customer_id);
      } else {
        throw new Error("Invalid payment method ID returned from Stripe.");
      }

    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error creating SetupIntent"
      );
      throw err;
    }
  };

  const chargeSavedCard = async (paymentMethodId: string, customerId?: string) => {
    // console.log("ChargeSavedCards In")
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/payments/stripe/charge-saved-card`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organization_id: supplierId,
          customer_id: customerId,
          payment_method_id: paymentMethodId,
          amount: Math.round(Number(totalAmount) * 100),
          currency: "usd"
        }),
      }
    );
    const data = await res.json();
    // console.log("ChargeSavedCard Response:", data);

    // Only throw if the response is not ok, or missing payment_intent_id
    if (!res.ok || !data.payment_intent_id) {
      throw new Error(data.error || "Failed to charge saved card");
    }
    // Update order payment status
    await handlePaymentSuccess(data.payment_intent_id);
    data.message && toast.success(`${data.message}`)
    // return data.data;
  };


  // Card UI Style (feel free to adjust!)
  const getCardBg = (brand: string) => {
    switch (brand) {
      case "Visa":
      case "visa":
        return "linear-gradient(135deg, #143d81 70%, #3262a5)";
      case "Mastercard":
      case "mastercard":
        return "linear-gradient(135deg, #ff9800 60%, #e91c24 100%)";
      case "amex":
      case "american express":
        return "linear-gradient(135deg, #0077A6 60%, #4AB0E3 100%)";
      case "Discover":
      case "discover":
        return "linear-gradient(135deg, #f76b1c 70%, #ffaf7b 100%)";
      case "diners":
      case "diners club":
        return "linear-gradient(135deg, #0061A8 60%, #ffffff 100%)";
      case "JCB":
      case "jcb":
        return "linear-gradient(135deg, #0a3b7e 60%, #c80027 100%)";
      case "UnionPay":
      case "unionpay":
        return "linear-gradient(135deg, #0aa1e1 60%, #78bc1f 100%)";
      default:
        return "linear-gradient(135deg, #343a40 60%, #636c72 100%)";
    }
  };


  //  Pay with a saved card
  const handlePayWithSavedCard = async (cardId: string) => {
    // console.log("handlePayWithSavedCard In")
    try {
      setLoading(true);
      // 1. Fetch saved card by id
      const card = await fetchSavedCardById(cardId);
      if (!card || !card.payment_method_id) throw new Error("Payment method not found for this card.");

      // 2. Charge the card
      // console.log("payment_method_id In handlePayWithSavedCard:", card.payment_method_id);
      await chargeSavedCard(card.payment_method_id, card.stripe_customer_id);

      toast.success("Payment successful!");
      // Optionally, call onPaymentSuccess if you want to advance the order flow
      // onPaymentSuccess(PASS_PAYMENT_INTENT_ID_HERE_IF_BACKEND_RETURNS_IT);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to pay with saved card.");
    } finally {
      setLoading(false);
    }
  };

  // Delete a saved card
  const handleDeleteCard = async (cardId: string) => {
    toast.info("Delete API coming soon! (Backend not ready)");
  };

  return (
    <div className="container">
      <ToastContainer />
      <form onSubmit={handlePaymentSubmit}>
        <h5>Enter Card Details</h5>
        <div className="mb-3">
          {/* <CardElement className="form-control mb-3 p-2" /> */}
          <PaymentElement
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
            }}
          />
        </div>
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="saveCardCheck"
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="saveCardCheck">
            Save card for future payments
          </label>
        </div>
        <button type="submit" className="btn btn-primary w-100" disabled={!stripe || loading}>
          {loading ? "Processing..." : `Pay $${totalAmount.toFixed(2)}`}
        </button>
      </form>
      <h5 className="my-3">Saved Cards</h5>
      {loadingCards ? (
        <div className="text-center">
          <div className="spinner-border" />
        </div>
      ) : savedCards.length === 0 ? (
        <div className="text-muted mb-4">No saved cards yet.</div>
      ) : (
        <div className="row">
          {savedCards.map((card) => (
            <div className="col-md-12 mb-4" key={card.id}>
              <div
                className="shadow card border-0 saved-card"
                style={{ background: getCardBg(card.brand) }}>
                {/* Overlay for hover */}
                <div className="card-overlay">
                  <button
                    className="btn btn-success overlay-btn"
                    onClick={() => handlePayWithSavedCard(card.id)}
                  >
                    Pay ${totalAmount.toFixed(2)}
                  </button>
                  <button
                    className="btn btn-danger overlay-btn"
                    onClick={() => handleDeleteCard(card.id)}
                  >
                    Delete
                  </button>
                </div>
                {/* Card content */}
                <div className="card-content">
                  <div className="d-flex align-items-center justify-content-between px-3 pt-3">
                    {brandLogo[card.brand as keyof typeof brandLogo] || brandLogo.Default}
                    {/* Card chip SVG */}
                    <svg width="36" height="24" viewBox="0 0 36 24">
                      <rect width="36" height="24" rx="6" fill="#fff" fillOpacity=".6" />
                      <rect x="6" y="6" width="24" height="12" rx="3" fill="#fff" fillOpacity=".5" />
                    </svg>
                  </div>
                  <div className="px-4 mt-4 mb-2" style={{ letterSpacing: "2px", fontSize: "1.25rem" }}>
                    **** **** **** {card.last4}
                  </div>
                  <div className="px-4 mb-3 d-flex justify-content-between align-items-end">
                    <div style={{ fontSize: ".95rem" }}>
                      <div>Exp: {card.exp_month.toString().padStart(2, "0")}/{card.exp_year}</div>
                      <div style={{ fontSize: ".82rem", opacity: 0.85 }}>
                        {card.brand}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      )}
    </div>
  );
};

export default PaymentForm;
